import { zodResolver } from '@hookform/resolvers/zod'
import { AlertTriangle, FileText, FileUp, Info, Loader2, Unlink } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Field, FormSection } from '@/components/common/FormBits'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { FormSelect, Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { fmtDate, fmtDateTime } from '../../../shared/format'
import { useMeetings, useSaveFile, useUnlinkFile } from '../../api'
import { FILE_CATEGORIES, UNLINK_REASONS, type CrmFile, type Customer } from '../../types'
import { toastError, useCrm } from '../../ui'
import { Dropzone } from '../Dropzone'

function Radios<V extends string>({ name, value, onChange, options }: { name: string; value: V; onChange: (v: V) => void; options: { value: V; label: string }[] }) {
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup">
      {options.map((o) => (
        <label key={o.value} className={cn('flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors', value === o.value ? 'border-primary/50 bg-primary/[0.06]' : 'hover:bg-muted/40')}>
          <input type="radio" name={name} className="accent-[var(--color-primary)]" checked={value === o.value} onChange={() => onChange(o.value)} />
          {o.label}
        </label>
      ))}
    </div>
  )
}

export function FileForm({ customer, file, onDone }: { customer: Customer; file: CrmFile | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveFile()
  const meetings = useMeetings(customer.id)
  const schema = z.object({
    category: z.enum(FILE_CATEGORIES),
    file: z.object({ name: z.string(), size: z.number(), file: z.instanceof(File).optional() }).nullable().refine((f) => !!f, t('crm.files.fileRequired')),
    title: z.string().trim().min(2, t('crm.files.titleRequired')).max(120),
    description: z.string().trim().max(400),
    visibility: z.enum(['team', 'all']),
    meetingId: z.string(),
  })
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      category: file?.category ?? 'contract',
      file: file ? { name: file.fileName, size: file.size } : null,
      title: file?.title ?? '',
      description: file?.description ?? '',
      visibility: file?.visibility ?? 'team',
      meetingId: file?.meetingId ?? '',
    },
  })
  const { errors } = form.formState
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync({
        id: file?.id,
        input: { customerId: customer.id, category: v.category, title: v.title, description: v.description, visibility: v.visibility, meetingId: v.meetingId || null, fileName: v.file?.name ?? '', size: v.file?.size ?? 0, file: v.file?.file },
      })
      toast.success(t('crm.files.saved'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })
  const meetingOptions = (meetings.data ?? []).filter((m) => m.customerId === customer.id).map((m) => ({ value: m.id, label: `${fmtDate(m.date)} · ${t(`crm.channel.${m.channel}`)}`, description: m.summary.slice(0, 60) }))

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<FileUp />}
      title={t(file ? 'crm.files.editTitle' : 'crm.files.uploadTitle')}
      description={t('crm.files.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <FormSection>
        <Field id="fl-cat" label={t('crm.files.category')} full>
          <Controller control={form.control} name="category" render={({ field }) => <Radios name="fl-cat" value={field.value} onChange={field.onChange} options={FILE_CATEGORIES.map((c) => ({ value: c, label: t(`crm.fileCategory.${c}`) }))} />} />
        </Field>
        <Field id="fl-file" label={t('crm.files.file')} error={errors.file?.message} full>
          <Controller
            control={form.control}
            name="file"
            render={({ field }) => <Dropzone id="fl-file" value={field.value} onChange={field.onChange} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" hint={t('crm.dropzone.docHint')} invalid={!!errors.file} />}
          />
        </Field>
        <Field id="fl-title" label={t('crm.files.title')} error={errors.title?.message} full>
          <Input id="fl-title" {...form.register('title')} />
        </Field>
        <Field id="fl-desc" label={t('crm.fields.description')} full>
          <Textarea id="fl-desc" rows={2} maxLength={400} {...form.register('description')} />
        </Field>
        <Field id="fl-vis" label={t('crm.files.visibility')}>
          <Controller
            control={form.control}
            name="visibility"
            render={({ field }) => (
              <div className="space-y-2" role="radiogroup">
                {(['team', 'all'] as const).map((v) => (
                  <label key={v} className="flex items-center gap-2 text-sm">
                    <input type="radio" name="fl-vis" className="accent-[var(--color-primary)]" checked={field.value === v} onChange={() => field.onChange(v)} />
                    {t(`crm.visibility.${v}`)}
                  </label>
                ))}
              </div>
            )}
          />
        </Field>
        <Field id="fl-meeting" label={t('crm.files.meeting')} hint={t('crm.files.meetingHint')}>
          <FormSelect id="fl-meeting" control={form.control} name="meetingId" options={[{ value: '', label: t('crm.optional') }, ...meetingOptions]} />
        </Field>
      </FormSection>
    </Modal>
  )
}

/** Belge silinmez; yalnız müşteri/proje ilişkisi kaldırılır. Neden zorunludur ve saklanır. */
export function UnlinkFileModal({ customer, file, onDone }: { customer: Customer; file: CrmFile; onDone: () => void }) {
  const { t } = useTranslation()
  const unlink = useUnlinkFile()
  const { projectName } = useCrm()
  const [reason, setReason] = useState<string>(UNLINK_REASONS[0])
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (reason === 'other' && note.trim().length < 3) {
      setError(t('crm.unlink.noteRequired'))
      return
    }
    try {
      await unlink.mutateAsync({ id: file.id, reason: `${t(`crm.unlinkReason.${reason}`)}${note.trim() ? ` — ${note.trim()}` : ''}` })
      toast.success(t('crm.unlink.done'))
      onDone()
    } catch (err) {
      toastError(err)
    }
  }
  return (
    <Modal
      open
      size="lg"
      tone="destructive"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!unlink.isPending}
      icon={<Unlink />}
      title={t('crm.unlink.title')}
      description={t('crm.unlink.hint')}
      onSubmit={(e) => void submit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="destructive" disabled={unlink.isPending}>
            {unlink.isPending ? <Loader2 className="animate-spin" /> : <Unlink />}
            {t('crm.unlink.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg border p-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
            <FileText className="size-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1 text-sm">
            <p className="flex flex-wrap items-center gap-2 font-medium">
              <span className="truncate">{file.fileName}</span>
              <Badge tone="info">{t(`crm.fileCategory.${file.category}`)}</Badge>
            </p>
            <p className="text-xs text-muted-foreground">
              {file.title} · {fmtDateTime(file.uploadedAt)} · {file.uploadedBy}
            </p>
          </div>
        </div>
        <div className="flex items-start gap-2 rounded-lg border border-info/30 bg-info/[0.06] p-3 text-sm">
          <Info className="mt-0.5 size-4 shrink-0 text-info" aria-hidden />
          <div>
            <p className="font-medium">{t('crm.unlink.linkedTo')}</p>
            <ul className="mt-1 list-disc pl-5 text-xs">
              <li>{t('crm.unlink.customer', { name: customer.name })}</li>
              <li>{t('crm.unlink.project', { name: projectName(customer.projectId) })}</li>
            </ul>
          </div>
        </div>
        <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/[0.08] p-3 text-sm">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
          <p>{t('crm.unlink.warning')}</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ul-reason">{t('crm.unlink.reason')}</Label>
          <Select id="ul-reason" value={reason} onChange={setReason} options={UNLINK_REASONS.map((r) => ({ value: r, label: t(`crm.unlinkReason.${r}`) }))} />
          <Textarea aria-label={t('crm.unlink.note')} rows={2} maxLength={240} placeholder={t('crm.unlink.notePlaceholder')} value={note} onChange={(e) => setNote(e.target.value)} />
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </div>
      </div>
    </Modal>
  )
}
